import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../../components/admin/AdminShell';
import LoadingDNA from '../../components/LoadingDNA';
import { KpiCard, MetaStrip, SectionCard, StatusBadge, TableControls } from '../../components/admin/AdminPrimitives';
import {
  formatCurrencyINRFull,
  formatDateIN,
  normalizeWorkflowStatus,
  workflowStatusLabel,
  workflowStatusTone,
} from '../../utils/adminFormat';
import { getCompanyEmployees, getTrips, isApiError } from '../../services/dashboardService';
import { bulkDecisionExpenseSplits, getAdminApprovalSlaBreaches, getExpenseEvents, isExpenseApiError, remindManagersForSplits } from '../../services/expenseService';

type ApprovalLine = {
  id: string;
  expenseEventId: string;
  category: string;
  merchant: string;
  amount: number;
  status: string;
  date?: string;
  employeeName: string;
  managerName: string;
  managerEmail?: string;
  department: string;
};

type TripGroup = {
  tripId: string;
  destination: string;
  purpose: string;
  date?: string;
  lines: ApprovalLine[];
  totalAmount: number;
  pendingCount: number;
};

const pendingStatuses = new Set(['pending']);
const SLA_BREACH_HOURS = 48;

const AdminTeamApprovalsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [tripGroups, setTripGroups] = useState<TripGroup[]>([]);
  const [slaBreaches, setSlaBreaches] = useState<Array<{
    split_id: string;
    trip_id?: string | null;
    employee_name?: string | null;
    manager_name?: string | null;
    department?: string | null;
    category?: string | null;
    split_amount: number;
    overdue_hours: number;
  }>>([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [selectedSplitIds, setSelectedSplitIds] = useState<string[]>([]);
  const [isSubmittingBulk, setIsSubmittingBulk] = useState(false);

  const loadData = async () => {
    const [events, employees, trips, breaches] = await Promise.all([
      getExpenseEvents({ page: 1, perPage: 500 }),
      getCompanyEmployees(),
      getTrips({ page: 1, perPage: 500 }),
      getAdminApprovalSlaBreaches({ page: 1, perPage: 300 }),
    ]);

    const employeeById = new Map(employees.map((item) => [item.id, item]));
    const tripById = new Map(trips.map((trip) => [trip.id, trip]));
    const grouped = new Map<string, TripGroup>();

    events.forEach((event) => {
      const splits = event.splits || [];
      splits.forEach((split) => {
        const key = event.trip_id;
        const existing = grouped.get(key) || {
          tripId: key,
          destination: tripById.get(key)?.destination || 'No destination',
          purpose: tripById.get(key)?.purpose || 'No purpose',
          date: tripById.get(key)?.start_date || event.created_at,
          lines: [],
          totalAmount: 0,
          pendingCount: 0,
        };
        const employee = employeeById.get(split.beneficiary_employee_id);
        const status = String(split.status || 'pending').toLowerCase();
        const line: ApprovalLine = {
          id: split.id,
          expenseEventId: event.id,
          category: event.category || 'Expense Share',
          merchant: event.merchant || '-',
          amount: Number(split.split_amount || 0),
          status,
          date: split.created_at || event.created_at,
          employeeName: employee?.name || employee?.email || 'Unknown employee',
          managerName: employee?.manager_name || 'Unassigned',
          managerEmail: employee?.manager_email || undefined,
          department: split.department || employee?.department || 'No department',
        };
        existing.lines.push(line);
        existing.totalAmount += line.amount;
        if (pendingStatuses.has(status)) existing.pendingCount += 1;
        grouped.set(key, existing);
      });
    });

    const groups = Array.from(grouped.values())
      .filter((group) => group.lines.length > 0)
      .sort((a, b) => b.pendingCount - a.pendingCount || b.totalAmount - a.totalAmount);

    setTripGroups(groups);
    setSlaBreaches(
      breaches.map((row) => ({
        split_id: row.split_id,
        trip_id: row.trip_id,
        employee_name: row.employee_name,
        manager_name: row.manager_name,
        department: row.department,
        category: row.category,
        split_amount: Number(row.split_amount || 0),
        overdue_hours: Number(row.overdue_hours || 0),
      })),
    );
    setLastRefreshedAt(new Date());
    setSelectedSplitIds([]);
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);
        await loadData();
      } catch (error) {
        if (isExpenseApiError(error)) setErrorMessage(error.message);
        else if (isApiError(error)) setErrorMessage(error.message);
        else if (error instanceof Error) setErrorMessage(error.message);
        else setErrorMessage('Failed to load team approvals.');
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  const pendingTrips = useMemo(() => tripGroups.filter((group) => group.pendingCount > 0).length, [tripGroups]);
  const pendingExpenses = useMemo(() => tripGroups.reduce((sum, group) => sum + group.pendingCount, 0), [tripGroups]);
  const pendingAmount = useMemo(
    () => tripGroups.reduce((sum, group) => sum + group.lines.filter((line) => normalizeWorkflowStatus(line.status) === 'pending').reduce((s, line) => s + line.amount, 0), 0),
    [tripGroups]
  );

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tripGroups
      .map((group) => {
        const lines = group.lines.filter((line) => {
          const status = normalizeWorkflowStatus(line.status);
          if (statusFilter !== 'all' && status !== statusFilter) return false;
          if (!q) return true;
          const haystack = [
            line.id,
            line.employeeName,
            line.managerName,
            line.department,
            line.category,
            line.merchant,
            group.tripId,
            group.destination,
            group.purpose,
          ].join(' ').toLowerCase();
          return haystack.includes(q);
        });
        if (lines.length === 0) return null;
        return { ...group, lines };
      })
      .filter(Boolean) as TripGroup[];
  }, [search, statusFilter, tripGroups]);
  const breachedSplitIds = useMemo(() => Array.from(new Set(slaBreaches.map((row) => row.split_id).filter(Boolean))), [slaBreaches]);

  const selectablePendingIds = useMemo(
    () => filteredGroups.flatMap((group) => group.lines.filter((line) => normalizeWorkflowStatus(line.status) === 'pending').map((line) => line.id)),
    [filteredGroups]
  );

  const toggleSplitSelection = (splitId: string): void => {
    setSelectedSplitIds((prev) => (prev.includes(splitId) ? prev.filter((id) => id !== splitId) : [...prev, splitId]));
  };

  const toggleSelectAllPending = (): void => {
    setSelectedSplitIds((prev) => {
      if (selectablePendingIds.length > 0 && selectablePendingIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !selectablePendingIds.includes(id));
      }
      const merged = new Set([...prev, ...selectablePendingIds]);
      return Array.from(merged);
    });
  };

  const runBulkAction = async (action: 'APPROVED' | 'REJECTED' | 'REMIND'): Promise<void> => {
    if (selectedSplitIds.length === 0) return;
    try {
      setIsSubmittingBulk(true);
      setErrorMessage(null);
      setInfoMessage(null);
      if (action === 'REMIND') {
        const result = await remindManagersForSplits({ split_ids: selectedSplitIds });
        setInfoMessage(`Reminder sent for ${result.sent} split(s).`);
      } else {
        const result = await bulkDecisionExpenseSplits({ split_ids: selectedSplitIds, decision: action });
        setInfoMessage(`${result.updated} split(s) updated. ${result.skipped} skipped.`);
      }
      await loadData();
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Bulk action failed.');
    } finally {
      setIsSubmittingBulk(false);
    }
  };

  const hasQueueData = tripGroups.length > 0;
  const pendingLinesForExport = useMemo(
    () => filteredGroups.flatMap((group) => group.lines.filter((line) => normalizeWorkflowStatus(line.status) === 'pending')),
    [filteredGroups],
  );

  const exportPendingPack = (): void => {
    const escapeCell = (value: string | number | null | undefined): string => {
      const text = String(value ?? '');
      if (text.includes(',') || text.includes('"') || text.includes('\n')) return `"${text.replace(/"/g, '""')}"`;
      return text;
    };
    const header = ['split_id', 'expense_event_id', 'employee', 'manager', 'department', 'category', 'merchant', 'amount', 'sla_age_hours', 'date'];
    const rows = pendingLinesForExport.map((line) => {
      const createdAt = line.date ? new Date(line.date).getTime() : Date.now();
      const ageHours = Math.max(0, Math.floor((Date.now() - createdAt) / (1000 * 60 * 60)));
      return [
        line.id,
        line.expenseEventId,
        line.employeeName,
        line.managerName,
        line.department,
        line.category,
        line.merchant,
        Number(line.amount || 0),
        ageHours,
        line.date || '',
      ];
    });
    const csv = [header.join(','), ...rows.map((row) => row.map((cell) => escapeCell(cell as string | number)).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', `pending-approvals-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const remindBreachedQueue = async (): Promise<void> => {
    if (breachedSplitIds.length === 0) return;
    try {
      setIsSubmittingBulk(true);
      const result = await remindManagersForSplits({ split_ids: breachedSplitIds });
      setInfoMessage(`Auto-reminders sent for ${result.sent} breached split(s).`);
      await loadData();
    } catch (error) {
      if (isExpenseApiError(error)) setErrorMessage(error.message);
      else if (error instanceof Error) setErrorMessage(error.message);
      else setErrorMessage('Failed to send SLA breach reminders.');
    } finally {
      setIsSubmittingBulk(false);
    }
  };

  return (
    <AdminShell pageTitle="Team Approvals" subtitle="Review trips first, then inspect expense lines within each trip.">
      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{errorMessage}</p> : null}
      {infoMessage ? <p className="rounded-lg bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">{infoMessage}</p> : null}

      {!isLoading ? (
        <div className="space-y-4">
          <MetaStrip
            left="Reporting period: current approval queue"
            right={`Last refreshed: ${lastRefreshedAt ? lastRefreshedAt.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}`}
          />

          <div className="grid gap-3 md:grid-cols-4">
            <KpiCard label="Trips in Queue" value={String(pendingTrips)} tone={pendingTrips > 0 ? 'warning' : 'positive'} />
            <KpiCard label="Pending Expense Lines" value={String(pendingExpenses)} tone={pendingExpenses > 0 ? 'warning' : 'positive'} />
            <KpiCard label="Pending Amount" value={formatCurrencyINRFull(pendingAmount)} tone={pendingAmount > 0 ? 'warning' : 'positive'} />
            <KpiCard label="SLA Breaches" value={String(slaBreaches.length)} tone={slaBreaches.length > 0 ? 'danger' : 'positive'} />
          </div>

          <SectionCard
            title="Approvals Action Queue"
            subtitle="Grouped by trip and sorted by pending count and amount."
            actions={
              hasQueueData ? (
                <div className="flex flex-wrap items-center gap-2">
                  <button type="button" onClick={exportPendingPack} className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                    Export Pending Pack
                  </button>
                  {selectedSplitIds.length > 0 ? <span className="text-xs font-semibold text-slate-600">{selectedSplitIds.length} selected</span> : null}
                  {selectedSplitIds.length > 0 ? <button type="button" disabled={isSubmittingBulk} onClick={() => runBulkAction('APPROVED')} className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">Bulk Approve</button> : null}
                  {selectedSplitIds.length > 0 ? <button type="button" disabled={isSubmittingBulk} onClick={() => runBulkAction('REJECTED')} className="rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white disabled:opacity-60">Bulk Reject</button> : null}
                  {selectedSplitIds.length > 0 ? <button type="button" disabled={isSubmittingBulk} onClick={() => runBulkAction('REMIND')} className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 disabled:opacity-60">Bulk Remind</button> : null}
                </div>
              ) : null
            }
          >
            {hasQueueData ? (
              <>
                <TableControls
                  searchValue={search}
                  onSearchChange={setSearch}
                  searchPlaceholder="Search trip, employee, manager, department, category..."
                  filters={(
                    <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Status
                      <select
                        value={statusFilter}
                        onChange={(event) => setStatusFilter(event.target.value as 'all' | 'pending' | 'approved' | 'rejected')}
                        className="h-9 rounded-lg border border-slate-300 bg-white px-2 text-sm font-semibold text-slate-700 outline-none focus:border-slate-500"
                      >
                        <option value="all">All</option>
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </label>
                  )}
                />

                {filteredGroups.length === 0 ? (
                  <p className="text-sm font-semibold text-slate-600">No approval lines match the selected filters.</p>
                ) : (
                  <div className="space-y-3">
                    {selectablePendingIds.length > 0 ? (
                      <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
                        <input
                          type="checkbox"
                          checked={selectablePendingIds.length > 0 && selectablePendingIds.every((id) => selectedSplitIds.includes(id))}
                          onChange={toggleSelectAllPending}
                        />
                        Select all pending lines in view
                      </label>
                    ) : null}

                    {filteredGroups.map((group) => (
                      <details key={group.tripId} className="rounded-xl border border-slate-200 bg-slate-50" open={group.pendingCount > 0}>
                        <summary className="cursor-pointer list-none px-4 py-3">
                          <div className="grid gap-2 md:grid-cols-[1.7fr_1fr_1fr_1fr] md:items-center">
                            <div>
                              <p className="text-sm font-bold text-slate-900">Trip {group.tripId.slice(0, 8)}</p>
                              <p className="text-sm font-medium text-slate-600">{group.destination} | {group.purpose}</p>
                            </div>
                            <p className="text-sm font-semibold text-slate-700">Items: {group.lines.length}</p>
                            <p className="text-sm font-semibold text-slate-700">Pending: {group.pendingCount}</p>
                            <p className="text-sm font-bold text-slate-900">{formatCurrencyINRFull(group.totalAmount)}</p>
                          </div>
                        </summary>

                        <div className="border-t border-slate-200 px-4 py-3">
                          <div className="overflow-x-auto">
                            <table className="w-full min-w-[1240px]">
                              <thead>
                                <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                                  <th className="pb-2">Select</th>
                                  <th className="pb-2">Line</th>
                                  <th className="pb-2">Employee</th>
                                  <th className="pb-2">Manager</th>
                                  <th className="pb-2">Department</th>
                                  <th className="pb-2">Category</th>
                                  <th className="pb-2 text-right">Amount</th>
                                  <th className="pb-2">Status</th>
                                  <th className="pb-2">SLA Age</th>
                                  <th className="pb-2">Date</th>
                                  <th className="pb-2 text-right">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {group.lines.map((line) => {
                                  const status = normalizeWorkflowStatus(line.status);
                                  const createdAt = line.date ? new Date(line.date).getTime() : Date.now();
                                  const ageHours = Math.max(0, Math.floor((Date.now() - createdAt) / (1000 * 60 * 60)));
                                  const isBreach = status === 'pending' && ageHours >= SLA_BREACH_HOURS;
                                  return (
                                    <tr key={line.id} className="border-b border-slate-100 text-sm text-slate-700">
                                      <td className="py-2.5">
                                        {status === 'pending' ? (
                                          <input
                                            type="checkbox"
                                            checked={selectedSplitIds.includes(line.id)}
                                            onChange={() => toggleSplitSelection(line.id)}
                                          />
                                        ) : null}
                                      </td>
                                      <td className="py-2.5 font-semibold text-slate-900">{line.id.slice(0, 8)}</td>
                                      <td className="py-2.5">{line.employeeName}</td>
                                      <td className="py-2.5">{line.managerName}</td>
                                      <td className="py-2.5">{line.department}</td>
                                      <td className="py-2.5">{line.category}</td>
                                      <td className="py-2.5 text-right font-semibold text-slate-900">{formatCurrencyINRFull(line.amount)}</td>
                                      <td className="py-2.5"><StatusBadge label={workflowStatusLabel(status)} tone={workflowStatusTone(status)} /></td>
                                      <td className="py-2.5">
                                        <div className="inline-flex items-center gap-2">
                                          <span>{ageHours}h</span>
                                          {isBreach ? <StatusBadge label="Breach" tone="danger" /> : null}
                                        </div>
                                      </td>
                                      <td className="py-2.5">{formatDateIN(line.date)}</td>
                                      <td className="py-2.5 text-right">
                                        <div className="inline-flex items-center gap-2">
                                          <Link
                                            to={`/admin/expense-events/${line.expenseEventId}`}
                                            className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-white"
                                          >
                                            View Event
                                          </Link>
                                          {status === 'pending' ? (
                                            <button
                                              type="button"
                                              onClick={async () => {
                                                try {
                                                  const result = await remindManagersForSplits({ split_ids: [line.id] });
                                                  setInfoMessage(`Reminder sent for ${result.sent} split(s).`);
                                                } catch (error) {
                                                  if (isExpenseApiError(error)) setErrorMessage(error.message);
                                                  else setErrorMessage('Failed to send reminder.');
                                                }
                                              }}
                                              className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100"
                                            >
                                              Remind
                                            </button>
                                          ) : null}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </details>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm font-semibold text-slate-600">No trips with expenses found in current queue.</p>
            )}
          </SectionCard>

          <SectionCard
            title="SLA Breach Drill-down"
            subtitle="Overdue approval lines with direct reminder action."
            actions={
              breachedSplitIds.length > 0 ? (
                <button
                  type="button"
                  onClick={remindBreachedQueue}
                  disabled={isSubmittingBulk}
                  className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800 disabled:opacity-60"
                >
                  Auto-Remind Breaches
                </button>
              ) : null
            }
          >
            {slaBreaches.length === 0 ? (
              <p className="text-sm font-semibold text-slate-600">No approval SLA breaches in the current queue.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Split</th>
                      <th className="pb-2">Trip</th>
                      <th className="pb-2">Employee</th>
                      <th className="pb-2">Manager</th>
                      <th className="pb-2">Department</th>
                      <th className="pb-2">Category</th>
                      <th className="pb-2 text-right">Amount</th>
                      <th className="pb-2">Overdue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slaBreaches.map((row) => (
                      <tr key={row.split_id} className="border-b border-slate-100 text-sm text-slate-700">
                        <td className="py-2.5 font-semibold text-slate-900">{row.split_id.slice(0, 8)}</td>
                        <td className="py-2.5">{row.trip_id ? row.trip_id.slice(0, 8) : '-'}</td>
                        <td className="py-2.5">{row.employee_name || 'Employee'}</td>
                        <td className="py-2.5">{row.manager_name || 'Manager'}</td>
                        <td className="py-2.5">{row.department || '-'}</td>
                        <td className="py-2.5">{row.category || '-'}</td>
                        <td className="py-2.5 text-right">{formatCurrencyINRFull(Number(row.split_amount || 0))}</td>
                        <td className="py-2.5">
                          <div className="inline-flex items-center gap-2">
                            <span>{row.overdue_hours}h</span>
                            <StatusBadge label="Breach" tone="danger" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminTeamApprovalsPage;
